import { createClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey, supabaseUrl } from '../utils/supabase/info';
import type { WooCommerceConfig } from './woocommerce';
import type { StoreConnectionType } from './storeConnections';

const supabase = createClient(
  supabaseUrl || `https://${projectId}.supabase.co`,
  publicAnonKey
);

const base = () => {
  const isLocal = supabaseUrl.includes('127.0.0.1') || supabaseUrl.includes('localhost');
  if (isLocal && typeof window !== 'undefined') {
    return `/supabase-api/functions/v1/make-server-75caf0c1`;
  }
  return supabaseUrl
    ? `${supabaseUrl}/functions/v1/make-server-75caf0c1`
    : `https://${projectId}.supabase.co/functions/v1/make-server-75caf0c1`;
};

const headers = async (json = true): Promise<Record<string, string>> => {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    apikey: publicAnonKey,
    Authorization: `Bearer ${session?.access_token || publicAnonKey}`,
  };
};

const parse = async (res: Response) => {
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    throw new Error(json?.error || `Erro do servidor (${res.status})`);
  }
  return json.data !== undefined ? json.data : json;
};

export const onboardingApi = {
  registerAccount: async (input: { email: string; password: string; firstName?: string; lastName?: string }) =>
    parse(await fetch(`${base()}/auth/register`, { method: 'POST', headers: await headers(), body: JSON.stringify(input) })),

  saveProgress: async (step: string, data: Record<string, any> = {}) =>
    parse(await fetch(`${base()}/onboarding/progress`, { method: 'POST', headers: await headers(), body: JSON.stringify({ step, data }) })),

  loadProgress: async () => {
    const res = await fetch(`${base()}/onboarding/progress`, { headers: await headers(false) });
    if (!res.ok) return null;
    const json = await res.json().catch(() => ({}));
    return json?.success ? json.data : null;
  },

  provisionStore: async (input: {
    name: string;
    connectionType: StoreConnectionType;
    wpUrl?: string;
    consumerKey?: string;
    consumerSecret?: string;
    currency?: string;
    slug?: string;
    onboardingCompleted?: boolean;
  }) => parse(await fetch(`${base()}/stores/provision`, { method: 'POST', headers: await headers(), body: JSON.stringify(input) })),

  createPairingCode: async (storeName?: string) =>
    parse(await fetch(`${base()}/pairing/codes`, { method: 'POST', headers: await headers(), body: JSON.stringify({ storeName }) })),

  getPairingStatus: async (code: string) =>
    parse(await fetch(`${base()}/pairing/codes/${encodeURIComponent(code)}/status`, { headers: await headers(false) })),
};

/** Sign a freshly registered user into a Supabase session. */
export const signInNewAccount = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error || !data.session) throw new Error(error?.message || 'Falha ao iniciar sessão.');
  return data.session;
};

/**
 * Registers a provisioned store in the local multi-store list so the
 * existing app shell (store switcher, active config, dashboard) picks it up
 * exactly like a store added manually in Settings.
 */
export const activateStoreLocally = async (store: { id: string; slug: string; name: string; connection_type: StoreConnectionType }, config: Partial<WooCommerceConfig>) => {
  const entry = {
    id: store.id,
    name: store.name,
    config: {
      version: 'v3',
      useProxy: true,
      url: '',
      consumerKey: '',
      consumerSecret: '',
      ...config,
      connectionType: store.connection_type,
      nativeStoreId: store.connection_type === 'native' ? store.id : undefined,
    } as WooCommerceConfig,
  };

  let multi: any[] = [];
  try {
    multi = JSON.parse(localStorage.getItem('wc_multistore') || '[]');
    if (!Array.isArray(multi)) multi = [];
  } catch { multi = []; }

  // Merge the shared server list into the local one before writing back —
  // a fresh account's empty local list must not wipe the shared multistore.
  try {
    const res = await fetch(`${base()}/kv/global_multistore`, { headers: await headers(false) });
    const json = await res.json().catch(() => ({}));
    const remote = Array.isArray(json?.data) ? json.data : [];
    if (remote.length) {
      const byId = new Map<string, any>();
      for (const s of remote) if (s?.id) byId.set(s.id, s);
      for (const s of multi) if (s?.id) byId.set(s.id, s);
      multi = [...byId.values()];
    }
  } catch { /* best effort — local list still saved below */ }

  const without = multi.filter((s: any) => s.id !== store.id);
  const next = [...without, entry];
  localStorage.setItem('wc_multistore', JSON.stringify(next));
  localStorage.setItem('wc_active_store_id', store.id);
  localStorage.setItem('wcactivestoreid', store.id);
  localStorage.setItem('wc_api_config', JSON.stringify(entry.config));
  localStorage.setItem('allowed_stores', JSON.stringify(next.map((s: any) => s.id)));

  // Keep the server-side shared list in sync (same mechanism as Settings).
  try {
    const { data: { session } } = await supabase.auth.getSession();
    await fetch(`${base()}/kv/global_multistore`, {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ data: next }),
    }).catch(() => {});
    void session;
  } catch { /* best effort — local list already written */ }

  window.dispatchEvent(new Event('storage'));
  window.dispatchEvent(new CustomEvent('store-switched'));
  window.dispatchEvent(new CustomEvent('config-updated', { detail: entry.config }));
  return entry;
};

export const hasActiveSession = async (): Promise<boolean> => {
  // Onboarding/provisioning requires a real Supabase Auth session — the
  // legacy `app_logged_in` flag alone is not enough (anon-key auth resolves
  // to a custom system user, not a provisionable account).
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session?.access_token;
  } catch {
    return false;
  }
};

export { supabase as onboardingSupabase };
